import { useState, useMemo, useRef } from 'react';
import { useProduct } from '@/api/inventory-management/products/useProduct';
import useFilteredProductAttributes from '@/api/inventory-management/products/useFilteredProductAttributes';
import useOrganizationConfig from '@/api/core/organization-config/useOrganizationConfig';
import useProductVariant from '@/api/inventory-management/products/useProductVariant';
import { sortProductAttributesByOrder } from '@/api/inventory-management/products/utils';

export interface UseProductAttributeSelectionProps {
  selectedProductId?: string;
  selectedVariantId?: string;
  customerId?: string;
}

export function useProductAttributeSelection({
  selectedProductId,
  selectedVariantId,
  customerId,
}: UseProductAttributeSelectionProps) {
  // Initialize state with proper default calculation
  const getInitialState = useMemo(() => {
    return () => ({
      selectedAttributes: {} as Record<string, string>,
      selectedAttributeOptionsByAttrId: {} as Record<string, string>,
    });
  }, []); // Static factory function

  const [selectedAttributes, setSelectedAttributes] = useState(
    getInitialState().selectedAttributes,
  );
  const [
    selectedAttributeOptionsByAttrId,
    setSelectedAttributeOptionsByAttrId,
  ] = useState(getInitialState().selectedAttributeOptionsByAttrId);

  // Track what product/variant combination we've initialized for
  const initializedForRef = useRef<string>('');
  // Track if user has made any edits so we prefer explicit user state even if empty
  const userEditedAttributesRef = useRef<boolean>(false);
  const userEditedEnumOptionsRef = useRef<boolean>(false);

  // Reset state when product changes (using the same pattern as useState initialization)
  const [lastProductId, setLastProductId] = useState(selectedProductId);
  if (lastProductId !== selectedProductId) {
    setLastProductId(selectedProductId);
    setSelectedAttributes({});
    setSelectedAttributeOptionsByAttrId({});
    initializedForRef.current = ''; // Reset initialization tracking
    userEditedAttributesRef.current = false;
    userEditedEnumOptionsRef.current = false;
  }

  // Fetch current product data
  const {
    data: currentProductData,
    isLoading: isLoadingCurrentProduct,
    error: currentProductError,
  } = useProduct(
    selectedProductId,
    { includes: ['attributes'] },
    !!selectedProductId,
  );

  // Organization config (contains optional attribute ordering schema)
  const { data: orgConfigData, isLoading: isLoadingOrgConfig } =
    useOrganizationConfig();

  // Fetch selected product variant data for prepopulation
  const {
    data: selectedVariantData,
    isLoading: isLoadingSelectedVariant,
    error: selectedVariantError,
  } = useProductVariant(selectedVariantId || '', {
    enabled: !!selectedVariantId,
  });

  // We'll define the filtered attributes query after calculating effective state

  // Cache attributes to maintain UI stability - calculated during rendering
  const lastKnownAttributesRef = useRef(
    currentProductData?.product?.attributes || [],
  );

  // Move rawAttributes calculation after filteredAttributesData is defined

  // Resolve preferred order: product-specific > global
  const preferredOrder: string[] | null = useMemo(() => {
    const schema = orgConfigData?.organizationConfig?.productAttributeOrder;
    if (!schema) return null;
    const productId = selectedProductId || currentProductData?.product?.id;

    if (productId && schema.byProductId && schema.byProductId[productId]) {
      return schema.byProductId[productId];
    }
    return schema.global ?? null;
  }, [
    orgConfigData?.organizationConfig?.productAttributeOrder,
    selectedProductId,
    currentProductData?.product?.id,
  ]);

  // Calculate variant attributes when data is available
  const calculatedVariantAttributes = useMemo(() => {
    if (
      !selectedVariantData?.productVariant ||
      !currentProductData?.product?.attributes
    ) {
      return null;
    }

    const variantAttributes = selectedVariantData.productVariant
      .attributes as Record<string, string>;
    const productAttributes = currentProductData.product.attributes;

    const newSelectedAttributes: Record<string, string> = {};
    const newSelectedAttributeOptionsByAttrId: Record<string, string> = {};

    // For each attribute in the variant, find the corresponding product attribute definition
    productAttributes.forEach((productAttr) => {
      // Check if this variant has a value for this attribute
      const variantAttrValue = variantAttributes[productAttr.id];

      if (variantAttrValue && productAttr.customField.fieldType === 'enum') {
        // For enum attributes, find the enum option that matches the variant's value
        const enumOptions = productAttr.customField.coreEnum?.options || [];
        const matchingOption = enumOptions.find(
          (option) => option.id === variantAttrValue,
        );

        if (matchingOption) {
          // Set the option ID for the filtered attributes API
          newSelectedAttributeOptionsByAttrId[productAttr.id] =
            matchingOption.id;

          // Set the origin-based selection for variant matching
          if (matchingOption.originEntity && matchingOption.originId) {
            newSelectedAttributes[matchingOption.originEntity] =
              matchingOption.originId;
          }
        }
      } else if (
        variantAttrValue &&
        productAttr.customField.fieldType !== 'enum'
      ) {
        // For non-enum attributes, set the value directly
        newSelectedAttributes[productAttr.customField.fieldName] =
          String(variantAttrValue);
      }
    });

    return {
      selectedAttributes: newSelectedAttributes,
      selectedAttributeOptionsByAttrId: newSelectedAttributeOptionsByAttrId,
    };
  }, [
    selectedVariantData?.productVariant,
    currentProductData?.product?.attributes,
  ]);

  // Compute the effective state that includes variant initialization
  const effectiveSelectedAttributes = useMemo(() => {
    // If user has interacted with attributes, prefer explicit user state (even if empty)
    if (userEditedAttributesRef.current) {
      return selectedAttributes;
    }

    // Otherwise, if we have calculated variant attributes and haven't been initialized manually, use those
    if (
      calculatedVariantAttributes &&
      Object.keys(calculatedVariantAttributes.selectedAttributes).length > 0
    ) {
      return calculatedVariantAttributes.selectedAttributes;
    }

    // Default to empty
    return {};
  }, [selectedAttributes, calculatedVariantAttributes]);

  const effectiveSelectedAttributeOptionsByAttrId = useMemo(() => {
    // If user has interacted with enum options, prefer explicit user state (even if empty)
    if (userEditedEnumOptionsRef.current) {
      return selectedAttributeOptionsByAttrId;
    }

    // Otherwise, if we have calculated variant attributes and haven't been initialized manually, use those
    if (
      calculatedVariantAttributes &&
      Object.keys(calculatedVariantAttributes.selectedAttributeOptionsByAttrId)
        .length > 0
    ) {
      return calculatedVariantAttributes.selectedAttributeOptionsByAttrId;
    }

    // Default to empty
    return {};
  }, [selectedAttributeOptionsByAttrId, calculatedVariantAttributes]);

  // No-op placeholder removed: initialization now handled inline in handlers using calculatedVariantAttributes

  // Fetch filtered attributes (remaining options) based on current enum selections
  const {
    data: filteredAttributesData,
    isLoading: isLoadingFilteredAttributes,
    error: filteredAttributesError,
  } = useFilteredProductAttributes(
    selectedProductId,
    {
      selectedAttributes:
        Object.keys(effectiveSelectedAttributeOptionsByAttrId).length > 0
          ? effectiveSelectedAttributeOptionsByAttrId
          : undefined,
      customerId,
    },
    !!selectedProductId,
  );

  const error =
    currentProductError || filteredAttributesError || selectedVariantError;

  // Calculate raw attributes now that we have filteredAttributesData
  const rawAttributes = useMemo(() => {
    const next =
      filteredAttributesData?.attributes ||
      currentProductData?.product?.attributes ||
      lastKnownAttributesRef.current ||
      [];

    // Update cache when we have a non-empty list
    if (next && next.length > 0) {
      lastKnownAttributesRef.current = next;
    }

    return next;
  }, [
    filteredAttributesData?.attributes,
    currentProductData?.product?.attributes,
  ]);

  const { visibleAttributes, hiddenAttributes } = useMemo(() => {
    const visible: typeof rawAttributes = [];
    const hidden: typeof rawAttributes = [];
    rawAttributes.forEach((attr) => {
      const enumOptions = attr.customField.coreEnum?.options || [];
      const noOptions =
        attr.customField.fieldType === 'enum' && enumOptions.length === 0;
      if (noOptions) hidden.push(attr);
      else visible.push(attr);
    });
    const sortedVisible = sortProductAttributesByOrder(visible, preferredOrder);
    return { visibleAttributes: sortedVisible, hiddenAttributes: hidden };
  }, [rawAttributes, preferredOrder]);

  const handleAttributeChange = (
    attributeName: string,
    value: string | null,
  ) => {
    userEditedAttributesRef.current = true;
    // Build from first-available source to avoid reversion on first edit
    const baseAttributes =
      Object.keys(selectedAttributes).length > 0
        ? selectedAttributes
        : calculatedVariantAttributes?.selectedAttributes || {};
    const updatedAttributes = { ...baseAttributes };

    if (value !== null && value !== undefined) {
      updatedAttributes[attributeName] = value;
    } else {
      delete updatedAttributes[attributeName];
    }

    setSelectedAttributes(updatedAttributes);
  };

  const handleEnumSelectionByAttrId = (
    productAttributeId: string,
    optionId: string | null,
  ) => {
    userEditedEnumOptionsRef.current = true;
    setSelectedAttributeOptionsByAttrId((prev) => {
      // On first interaction, base the map on calculated variant options
      // so clearing works even when actual state is empty
      const base =
        Object.keys(prev).length > 0
          ? prev
          : calculatedVariantAttributes?.selectedAttributeOptionsByAttrId || {};
      const next = { ...base };
      if (optionId) {
        next[productAttributeId] = optionId;
      } else {
        delete next[productAttributeId];
      }
      return next;
    });
  };

  const resetAttributeSelection = () => {
    userEditedAttributesRef.current = true;
    userEditedEnumOptionsRef.current = true;
    setSelectedAttributes({});
    setSelectedAttributeOptionsByAttrId({});
  };

  return {
    // State (using effective state that includes variant initialization)
    selectedAttributes: effectiveSelectedAttributes,
    selectedAttributeOptionsByAttrId: effectiveSelectedAttributeOptionsByAttrId,

    // Computed values
    visibleAttributes,
    hiddenAttributes,
    currentProductData,

    // Loading states
    isLoadingCurrentProduct,
    isLoadingOrgConfig,
    isLoadingFilteredAttributes,
    isLoadingSelectedVariant,
    isLoading:
      isLoadingCurrentProduct ||
      isLoadingOrgConfig ||
      isLoadingFilteredAttributes ||
      isLoadingSelectedVariant,

    // Error state
    error,

    // Actions
    handleAttributeChange,
    handleEnumSelectionByAttrId,
    resetAttributeSelection,
  };
}
